/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, type ReactNode } from 'react';
import type { SnippetsSectionProps } from '../Snippets/types';
import { groupByInstallPackage } from '../../../utils/groupByInstallPackage/groupByInstallPackage';
import { HighlightCode } from '../../HighlightCode/HighlightCode';
import { Link } from '@mui/material';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Text } from './Text';
import { SECTION_NAME_INSTALL } from '../../../constants/snippetsSectionNames';
import { EditorSettingsContext } from '../../../contexts/EditorSettingsContext';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

export function Install( {
	isLoading,
	number,
	onCopyEvent
}: SnippetsSectionProps ): ReactNode {
	const { selectedTechnology, selectedInstallMethod } = useContext( InstallationSettingsContext );
	const { editorPlugins } = useContext( EditorSettingsContext );
	const { 'ckeditor5-premium-features': ckeditor5PremiumFeatures } = groupByInstallPackage( editorPlugins );

	const packagesToInstall: Array<string> = [];

	// See https://github.com/ckeditor/ckeditor5-builder/issues/1136
	if ( selectedInstallMethod === 'cdn' && selectedTechnology === 'angular' ) {
		packagesToInstall.push( 'ckeditor5 --save-dev' );

		if ( ckeditor5PremiumFeatures.length ) {
			packagesToInstall.push( 'ckeditor5-premium-features --save-dev' );
		}
	}
	// Regardless of the installation method, Angular needs the `ckeditor5` and `ckeditor5-premium-features` packages to access the typings.
	else if ( selectedInstallMethod === 'npm' || selectedTechnology === 'angular' ) {
		packagesToInstall.push( 'ckeditor5' );

		if ( ckeditor5PremiumFeatures.length ) {
			packagesToInstall.push( 'ckeditor5-premium-features' );
		}
	}

	if ( selectedTechnology === 'react' ) {
		packagesToInstall.push( '@ckeditor/ckeditor5-react' );
	}

	if ( selectedTechnology === 'vue' ) {
		packagesToInstall.push( '@ckeditor/ckeditor5-vue' );
	}

	if ( selectedTechnology === 'angular' ) {
		packagesToInstall.push( '@ckeditor/ckeditor5-angular' );
	}

	return (
		<SkeletonLoader number={ number } sectionTitle={ SECTION_NAME_INSTALL } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ SECTION_NAME_INSTALL }>
				<Text>
					To begin, install the necessary npm packages.
				</Text>
				<Text>
					<b>Note:</b> CKEditor 5 is compatible with any modern JavaScript bundler. For a quick sample project setup, we recommend
					using <Link target='_blank' href='https://vitejs.dev/guide/'>Vite</Link>.
				</Text>
				<HighlightCode onCopyEvent={ onCopyEvent } lang={ 'bash' } id={ 'snippet-bash' }>
					{ packagesToInstall.map( packageName => `npm install ${ packageName }` ).join( '\n' ) }
				</HighlightCode>
			</SnippetsSection>
		</SkeletonLoader>
	);
}
